import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import {
  Header,
  Statistic,
  Container,
  Grid,
  Dropdown,
  Image,
  Message,
  Loader,
} from 'semantic-ui-react';

import {useHttpClient} from 'hooks';

import styles from './MatomoStatistics.module.scss';

const visitsTimeRangeOptions = [
  {
    key: 'today',
    text: 'Today',
    value: 'today',
  },
  {
    key: 'thisWeek',
    text: 'This Week',
    value: 'last7',
  },
  {
    key: 'last14',
    text: 'Last 2 Weeks',
    value: 'last14',
  },
  {
    key: 'last30',
    text: 'Last Month',
    value: 'last30',
  },
  {
    key: 'last180',
    text: 'Last 6 Months',
    value: 'last180',
  },
];

const graphTimeRangeOptions = [
  {
    key: 'thisWeek',
    text: 'This Week',
    value: 'last7',
  },
  {
    key: 'last14',
    text: 'Last 2 Weeks',
    value: 'last14',
  },
  {
    key: 'last30',
    text: 'Last Month',
    value: 'last30',
  },
  {
    key: 'last6',
    text: 'Last 6 Months',
    value: 'last6',
  },
];

const renderStatistics = (error, stats) => {
  if (error) {
    return <Message negative>There was a problem fetching the statistics from Matomo.</Message>;
  } else {
    return <Statistic.Group horizontal size="mini" items={processStatistics(stats.statistics)} />;
  }
};

const renderGraph = (error, graphData) => {
  if (error) {
    return <Message negative>There was a problem fetching the graph from Matomo.</Message>;
  } else {
    return <Image fluid src={URL.createObjectURL(graphData)} alt="visits_graph" />;
  }
};

const processStatistics = stats => {
  const processedStatistics = [
    {key: 'visits', label: 'Visits', value: stats.visits},
    {
      key: 'actionsPerVisit',
      label: 'Actions per visit',
      value: stats.actionsPerVisit,
    },
  ];

  if (stats.uniqueVisitors !== undefined) {
    processedStatistics.push({
      key: 'uniqueVisitors',
      label: 'Unique visitors',
      value: stats.uniqueVisitors,
    });
  }
  return processedStatistics;
};

const MatomoStatistics = ({siteId, columns}) => {
  const [visitsTimeRange, setVisitsTimeRange] = useState(visitsTimeRangeOptions[0].value);
  const [graphTimeRange, setGraphTimeRange] = useState(graphTimeRangeOptions[0].value);

  const {
    data: statsData,
    error: statsError,
    loading: isLoadingStats,
    refetch: refetchStats,
  } = useHttpClient({
    url: `/api/web-analytics/visits`,
    method: 'GET',
    params: {siteId, timeRange: visitsTimeRange},
  });

  const {
    data: graphData,
    error: graphError,
    loading: isLoadingGraph,
    refetch: refetchGraph,
  } = useHttpClient({
    url: `/api/web-analytics/graph`,
    method: 'GET',
    params: {siteId, timeRange: graphTimeRange},
    responseType: 'blob',
  });

  useEffect(() => {
    refetchStats();
  }, [visitsTimeRange, siteId, refetchStats]);

  useEffect(() => {
    refetchGraph();
  }, [graphTimeRange, siteId, refetchGraph]);

  return (
    <Container className={styles.MatomoStatistics}>
      <Grid columns={columns} stackable>
        <Grid.Column>
          <Header as="h4" textAlign="center" dividing>
            Visits Overview for{' '}
            <Dropdown
              floating
              inline
              options={visitsTimeRangeOptions}
              defaultValue={visitsTimeRangeOptions[0].value}
              onChange={(_evt, data) => setVisitsTimeRange(data.value)}
            />
          </Header>
          {isLoadingStats ? (
            <Loader inline="centered" active />
          ) : (
            renderStatistics(statsError, statsData)
          )}
        </Grid.Column>
        <Grid.Column>
          <Header as="h4" textAlign="center" dividing>
            Visits over{' '}
            <Dropdown
              floating
              inline
              options={graphTimeRangeOptions}
              defaultValue={graphTimeRangeOptions[0].value}
              onChange={(_evt, data) => setGraphTimeRange(data.value)}
            />
          </Header>
          {isLoadingGraph ? (
            <Loader inline="centered" active />
          ) : (
            renderGraph(graphError, graphData)
          )}
        </Grid.Column>
      </Grid>
    </Container>
  );
};

MatomoStatistics.propTypes = {
  siteId: PropTypes.number.isRequired,
  columns: PropTypes.number.isRequired,
};

export default MatomoStatistics;
